import React, { useEffect, useMemo, useState } from 'react'
import { Autocomplete, Avatar, Box, Chip, InputAdornment, TextField } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined'
import { useGetProjectTrackerProjects, useGetProjectTrackerStaff } from 'services/api/task-management/trackerHooks'
import { filterVisibleProjectTrackerStaff } from '../helpers/hiddenStaff'

const getImgUrl = (staff) =>
    staff?.staff_profile_image?.profile_image_url ??
    staff?.profile_image_url ??
    null

const getInitials = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return '??'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[1][0]).toUpperCase()
}

const getOptionKey = (item) => `${item?.type ?? 'unknown'}-${String(item?.id ?? '')}`
const normalizeText = (value) => String(value || '').trim().toLowerCase()

const getSearchableText = (item) => {
    return [
        item?.label,
        item?.name,
        item?.project_name,
        item?.work_email,
        item?.department,
        item?.description,
        item?.objective,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
}

const getMatchScore = (item, query) => {
    const normalizedQuery = normalizeText(query)
    if (!normalizedQuery) return 0

    const label = normalizeText(item?.label ?? item?.name ?? item?.project_name)
    const searchableText = getSearchableText(item)

    if (label === normalizedQuery) return 300
    if (label.startsWith(normalizedQuery)) return 200
    if (searchableText.includes(normalizedQuery)) return 100
    return -1
}

export default function TrackerCalendarSearch({
    selectedFilters = [],
    setSelectedFilters,
    hideInput = false,
}) {
    const [q, setQ] = useState('')
    const [debouncedQ, setDebouncedQ] = useState('')

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(q), 250)
        return () => clearTimeout(t)
    }, [q])

    const { data: staffData, isLoading: isStaffLoading } = useGetProjectTrackerStaff(
        { q: debouncedQ },
        { enabled: !hideInput }
    )
    const { data: projectsData, isLoading: isProjectsLoading } = useGetProjectTrackerProjects(
        {},
        { enabled: !hideInput }
    )

    const staffOptions = useMemo(() => {
        const grouped = staffData?.data?.staff
        if (!grouped) return []

        const flattened = Array.isArray(grouped)
            ? grouped
            : typeof grouped === 'object'
                ? Object.values(grouped).flat().filter(Boolean)
                : []

        return filterVisibleProjectTrackerStaff(
            flattened.filter((staff) => Number(staff?.employment_status) === 1)
        )
            .map((staff) => ({
                ...staff,
                type: 'employee',
                label: staff?.name ?? '',
            }))
    }, [staffData])

    const projectOptions = useMemo(() => {
        const projects = projectsData?.data?.projects ?? []

        return projects
            .map((project) => ({
                ...project,
                type: 'project',
                label: project?.name ?? project?.project_name ?? '',
            }))
    }, [projectsData])

    const searchOptions = useMemo(() => {
        const normalizedQuery = normalizeText(q)
        const combined = [...staffOptions, ...projectOptions]

        if (!normalizedQuery) return combined

        return combined
            .map((item, index) => ({
                item,
                index,
                score: getMatchScore(item, normalizedQuery),
            }))
            .filter(({ score }) => score >= 0)
            .sort((a, b) => {
                if (b.score !== a.score) return b.score - a.score
                return a.index - b.index
            })
            .map(({ item }) => item)
    }, [staffOptions, projectOptions, q])

    const pillSx = {
        height: 40,
        borderRadius: 999,
        backgroundColor: '#fff',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.2)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.2)' },
    }

    const handleRemove = (itemToRemove) => {
        setSelectedFilters((prev) =>
            prev.filter((item) => getOptionKey(item) !== getOptionKey(itemToRemove))
        )
    }

    if (hideInput) {
        return (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {selectedFilters.map((item) => {
                    const isEmployee = item?.type === 'employee'
                    const src = isEmployee ? getImgUrl(item) : null
                    return (
                        <Chip
                            key={getOptionKey(item)}
                            variant="outlined"
                            onDelete={() => handleRemove(item)}
                            label={item?.label ?? item?.name ?? item?.project_name ?? '-'}
                            avatar={isEmployee ? (
                                <Avatar
                                    src={src || undefined}
                                    imgProps={{ referrerPolicy: 'no-referrer' }}
                                    sx={{ width: 24, height: 24, fontSize: 11 }}
                                >
                                    {!src ? getInitials(item?.name) : null}
                                </Avatar>
                            ) : undefined}
                            icon={!isEmployee ? <WorkspacesOutlinedIcon sx={{ fontSize: 18 }} /> : undefined}
                            sx={{
                                borderRadius: 999,
                                height: 36,
                                px: 0.5,
                                '& .MuiChip-label': { fontWeight: 600 },
                            }}
                        />
                    )
                })}
            </Box>
        )
    }

    return (
        <Autocomplete
            multiple
            value={selectedFilters}
            options={searchOptions}
            loading={isStaffLoading || isProjectsLoading}
            clearOnEscape
            disableCloseOnSelect

            getOptionLabel={(opt) => opt?.label ?? opt?.name ?? opt?.project_name ?? ''}
            isOptionEqualToValue={(opt, val) => getOptionKey(opt) === getOptionKey(val)}

            onChange={(_, newValue) => {
                setSelectedFilters(newValue || [])
            }}

            inputValue={q}
            onInputChange={(_, newInput) => setQ(newInput)}

            renderTags={() => null}

            renderOption={(props, option) => {
                const isEmployee = option?.type === 'employee'
                const src = isEmployee ? getImgUrl(option) : null
                return (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isEmployee ? (
                            <Avatar
                                src={src || undefined}
                                imgProps={{ referrerPolicy: 'no-referrer' }}
                                sx={{ width: 28, height: 28, fontSize: 12 }}
                            >
                                {!src ? getInitials(option?.name) : null}
                            </Avatar>
                        ) : (
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(25,118,210,0.10)',
                                    color: 'primary.main',
                                    display: 'grid',
                                    placeItems: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <WorkspacesOutlinedIcon sx={{ fontSize: 18 }} />
                            </Box>
                        )}

                        <Box sx={{ minWidth: 0 }}>
                            <Box
                                sx={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {option?.label}
                            </Box>

                            {isEmployee && option?.work_email ? (
                                <Box
                                    sx={{
                                        fontSize: 12,
                                        color: 'text.secondary',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {option.work_email}
                                </Box>
                            ) : isEmployee && option?.department ? (
                                <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{option.department}</Box>
                            ) : !isEmployee ? (
                                <Box sx={{ fontSize: 12, color: 'text.secondary' }}>Project</Box>
                            ) : null}
                        </Box>
                    </Box>
                )
            }}

            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Search employees or projects"
                    size="small"
                    sx={{ minWidth: 320, '& .MuiOutlinedInput-root': pillSx }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            )}
        />
    )
}
